// AI 导演节点（legacyType: 'aiDirector'）：上下文装配 + 导演消息协议 + 改写建议解析
//
// 上下文来源：连入导演节点的 reference 边（不限对端类型），发送时动态装配。
// 多轮历史由后端 assistant conversation 维护（AGENT_CHAT + conversationId），
// data.messages 只保留最近 20 条作为节点卡片上的显示缓存（随快照/保存队列持久化）。

// 单节点提示词/内容摘要的截断长度
const PEER_TEXT_MAX = 200;
// 单条回复最多落地的改写建议条数（防御模型刷屏）
const MAX_SUGGESTIONS = 10;

// 连入节点类型 → 人话标签（覆盖画布现有类型，未知类型原样透出）
const TYPE_LABELS = {
  resource: "资源",
  generate: "生成",
  storyboardShot: "分镜",
  storyboardGrid: "分镜宫格",
  storyboardEpisode: "整集分镜",
  scriptToStoryboard: "剧本转分镜",
  seedanceMerge: "视频合并",
  motionStoryboard: "动态分镜",
  stickyNote: "便签",
  groupFrame: "分组框",
  aiDirector: "AI导演",
};

// 「重新生成选中节点」可直接复用 handleNodeGenerate 的类型
// （scriptToStoryboard 走专用回调、motionStoryboard 走编辑器，均不在列）
export const REGENERATABLE_LEGACY_TYPES = new Set([
  "generate",
  "storyboardShot",
  "storyboardGrid",
  "seedanceMerge",
  "storyboardEpisode",
]);

// 「优化相连节点提示词」的预制请求（userText），
// 消息气泡里显示的是短文案（由节点组件给定），不是这段完整指令
export const DIRECTOR_OPTIMIZE_REQUEST = [
  "请逐个优化当前连入节点的提示词：保留原意与主体设定，增强画面感、镜头感与可拍性",
  "（把光线、构图、材质、动作写具体）。对每个节点给出改写后的完整提示词，",
  "按约定的 json suggestions 格式返回，正文用一两句话说明整体思路。",
].join("");

// 收集连入导演节点的 reference 边源节点（不限类型，去重、保序）
export function collectDirectorPeers(nodeId, edges, nodes) {
  const incoming = (edges || []).filter(
    (e) => e.target === nodeId && (e.data?.connectionType || "reference") === "reference"
  );
  const seen = new Set();
  const peers = [];
  for (const e of incoming) {
    if (seen.has(e.source)) continue;
    seen.add(e.source);
    const peer = (nodes || []).find((n) => n.id === e.source);
    if (peer) peers.push(peer);
  }
  return peers;
}

const truncate = (text, max = PEER_TEXT_MAX) => {
  const t = String(text || "").trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
};

const getPeerName = (node) => {
  const d = node.data || {};
  const legacy = d.legacyType || node.type;
  if (d.name) return d.name;
  if (d.shotNumber != null) return `分镜#${d.shotNumber}`;
  return TYPE_LABELS[legacy] || "节点";
};

const getPeerStatus = (node) => {
  const d = node.data || {};
  if (d.isGenerating || d.generating || d.status === "generating") return "生成中";
  if (d.generatedImage || d.generatedVideo || d.lastResultUrl || d.gridImageUrl || d.result || d.url) return "已生成";
  return "待生成";
};

// 单节点摘要行：[id:xx | 名称 · 类型] 状态:xx | 提示词:xx | 内容:xx
export function summarizeDirectorPeer(node) {
  const d = node.data || {};
  const legacy = d.legacyType || node.type;
  const typeLabel = TYPE_LABELS[legacy] || legacy || "未知";
  const parts = [`[id:${node.id} | ${getPeerName(node)} · ${typeLabel}] 状态:${getPeerStatus(node)}`];
  const prompt = d.prompt || d.imagePrompt || d.videoPrompt || "";
  if (prompt.trim()) parts.push(`提示词:${truncate(prompt)}`);
  const content = d.text || d.scriptText || d.description || "";
  if (content.trim()) parts.push(`内容:${truncate(content)}`);
  return `- ${parts.join(" | ")}`;
}

// 画布上下文块（连入节点为空时给空态说明）
export function buildDirectorContextText(peers) {
  if (!peers || peers.length === 0) {
    return "【画布上下文】\n当前没有节点连入导演节点，只能泛泛回答；可提醒用户把分镜/图片/剧本节点的输出连到导演节点。";
  }
  const lines = peers.map(summarizeDirectorPeer);
  return `【画布上下文】（连入 ${peers.length} 个节点，可对其讨论或给出改写建议）\n${lines.join("\n")}`;
}

// 导演设定：每条消息携带（后端 history 有轮次截断，保证人设与输出协议不丢）
const DIRECTOR_PREAMBLE = [
  "你是钉在 Storyviz 画布上的 AI 导演，看着与用户画布相连的节点与用户讨论。",
  "用中文简洁直接地聊分镜节奏、画面与提示词，不要寒暄。",
  "当你要给出可直接落回节点的提示词改写建议时，在回复末尾附加一个 ```json 代码块，格式：",
  '{"suggestions":[{"nodeId":"上下文中的节点id","prompt":"改写后的完整提示词","reason":"一句话理由"}]}。',
  "nodeId 必须严格取自【画布上下文】中的节点 id，不要编造；没有改写建议时不要输出该代码块。",
].join("");

// 组装发送给 AGENT_CHAT 的完整消息（导演设定 + 画布上下文 + 用户消息）
export function buildDirectorMessage(userText, peers) {
  return [
    DIRECTOR_PREAMBLE,
    "",
    buildDirectorContextText(peers),
    "",
    "【用户消息】",
    userText,
  ].join("\n");
}

// 解析导演回复：剥离 suggestions 代码块，返回 { text, suggestions }
// suggestions: [{ nodeId, prompt, reason? }]（已过滤缺字段项，未校验节点存在性——由应用时校验）
export function parseDirectorSuggestions(replyText) {
  const raw = typeof replyText === "string" ? replyText : "";
  if (!raw) return { text: "", suggestions: [] };

  const normalize = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((s) => s && s.nodeId != null && String(s.nodeId).trim() && typeof s.prompt === "string" && s.prompt.trim())
      .slice(0, MAX_SUGGESTIONS)
      .map((s) => ({ nodeId: String(s.nodeId), prompt: s.prompt.trim(), reason: typeof s.reason === "string" ? s.reason.trim() : "" }));

  // 1) ```json ...``` 代码块
  const fenceRe = /```(?:json)?\s*([\s\S]*?)```/g;
  let match;
  while ((match = fenceRe.exec(raw)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim());
      const suggestions = normalize(parsed?.suggestions);
      if (suggestions.length > 0) {
        return { text: raw.replace(match[0], "").trim(), suggestions };
      }
    } catch { /* 非建议 JSON 块，保留原文继续找 */ }
  }

  // 2) 兜底：裸 {"suggestions":[...]}（模型没用代码块时）
  const bare = raw.match(/\{\s*"suggestions"\s*:[\s\S]*\}\s*$/);
  if (bare) {
    try {
      const parsed = JSON.parse(bare[0]);
      const suggestions = normalize(parsed?.suggestions);
      if (suggestions.length > 0) {
        return { text: raw.replace(bare[0], "").trim(), suggestions };
      }
    } catch { /* 解析失败按无建议处理 */ }
  }

  return { text: raw, suggestions: [] };
}
